import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import RoomVoiceEntry from './RoomVoiceEntry';
import './P2PChat.css';
import './RoomChat.css';

const NOTIFICATION_MUTE_OPTIONS = [
  { label: '30 minutes', minutes: 30 },
  { label: '2 hours', minutes: 120 },
  { label: '6 hours', minutes: 360 },
  { label: 'Forever', minutes: null },
];
const MESSAGE_PAGE_SIZE = 30;

function getAvatarColor(id) {
  const palette = ['#7c3aed', '#2563eb', '#059669', '#b45309', '#db2777', '#0891b2', '#dc2626', '#65a30d'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function groupMessages(messages) {
  const groups = [];
  const FIVE_MIN_MS = 5 * 60 * 1000;
  for (const msg of messages) {
    const last = groups[groups.length - 1];
    const lastMsg = last?.messages[last.messages.length - 1];
    if (last && last.sender_id === msg.sender_id &&
        new Date(msg.created_at) - new Date(lastMsg.created_at) < FIVE_MIN_MS) {
      last.messages.push(msg);
    } else {
      groups.push({ sender_id: msg.sender_id, messages: [msg] });
    }
  }
  return groups;
}

const RoomChat = ({
  chatId,
  accessChatId = chatId,
  session,
  user,
  voicePresence,
  onBack,
  onMembersChange,
  onDeleted,
  onLeft,
  isFavorite,
  onToggleFavorite,
  isNotificationsMuted,
  onMuteNotifications,
  onUnmuteNotifications,
}) => {
  const [chat, setChat] = useState(null);
  const [membership, setMembership] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [deleteTargetMessage, setDeleteTargetMessage] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(false);

  // Topbar menu
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showNotificationMuteModal, setShowNotificationMuteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const menuRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messagesViewportRef = useRef(null);
  const inputRef = useRef(null);
  const profileCache = useRef({});
  const channelRef = useRef(null);
  const shouldStickToBottomRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);

  useEffect(() => {
    initialLoadCompleteRef.current = false;
    shouldStickToBottomRef.current = false;
    loadRoom();
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [chatId, accessChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldStickToBottomRef.current || !messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: initialLoadCompleteRef.current ? 'smooth' : 'auto' });
    shouldStickToBottomRef.current = false;
    initialLoadCompleteRef.current = true;
  }, [messages]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (!e.target.closest('.message-actions')) {
        setActiveMessageMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadRoom = async () => {
    const [chatRes, membershipRes, messagesRes, membersRes] = await Promise.all([
      supabase.from('chats').select('*').eq('id', chatId).single(),
      supabase.from('chat_members').select('*').eq('chat_id', accessChatId).eq('user_id', user.id).single(),
      supabase
        .from('chat_messages')
        .select('*, profiles!chat_messages_sender_id_profiles_fkey(id, username, display_name)')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: false })
        .limit(MESSAGE_PAGE_SIZE),
      supabase
        .from('chat_members')
        .select('*, profiles!chat_members_user_id_profiles_fkey(id, username, display_name)')
        .eq('chat_id', accessChatId),
    ]);

    setChat(chatRes.data);
    setMembership(membershipRes.data);

    const memberList = membersRes.data || [];
    memberList.forEach(m => { if (m.profiles) profileCache.current[m.user_id] = m.profiles; });

    const initialMessages = (messagesRes.data || []).slice().reverse();
    shouldStickToBottomRef.current = true;
    setMessages(initialMessages);
    setHasMoreMessages((messagesRes.data || []).length === MESSAGE_PAGE_SIZE);

    if (onMembersChange) onMembersChange(memberList, membershipRes.data);

    // Mark all messages as read now that they are visible
    supabase.rpc('mark_chat_read', { p_chat_id: chatId }).then(() => {});

    const channel = supabase
      .channel(`room-${chatId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `chat_id=eq.${chatId}` },
        async (payload) => {
          const msg = payload.new;
          if (msg.sender_id === user.id) return;
          const profile = profileCache.current[msg.sender_id] || await fetchProfile(msg.sender_id);
          shouldStickToBottomRef.current = true;
          setMessages(prev => [...prev, { ...msg, profiles: profile }]);
          // Mark as read since the user is actively viewing this room
          supabase.rpc('mark_chat_read', { p_chat_id: chatId }).then(() => {});
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'chat_messages' },
        (payload) => {
          setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
        }
      )
      .subscribe();

    channelRef.current = channel;
  };

  const fetchProfile = async (userId) => {
    const { data } = await supabase.from('profiles').select('id, username, display_name').eq('id', userId).single();
    if (data) profileCache.current[userId] = data;
    return data;
  };

  const loadOlderMessages = async () => {
    if (loadingOlderMessages || !hasMoreMessages || messages.length === 0 || !messagesViewportRef.current) return;

    setLoadingOlderMessages(true);
    const viewport = messagesViewportRef.current;
    const previousScrollHeight = viewport.scrollHeight;
    const previousScrollTop = viewport.scrollTop;
    const oldestMessageCreatedAt = messages[0]?.created_at;

    const { data } = await supabase
      .from('chat_messages')
      .select('*, profiles!chat_messages_sender_id_profiles_fkey(id, username, display_name)')
      .eq('chat_id', chatId)
      .lt('created_at', oldestMessageCreatedAt)
      .order('created_at', { ascending: false })
      .limit(MESSAGE_PAGE_SIZE);

    const olderMessages = (data || []).slice().reverse();
    setHasMoreMessages((data || []).length === MESSAGE_PAGE_SIZE);

    if (olderMessages.length > 0) {
      setMessages(prev => [...olderMessages, ...prev]);
      requestAnimationFrame(() => {
        if (!messagesViewportRef.current) return;
        const nextScrollHeight = messagesViewportRef.current.scrollHeight;
        messagesViewportRef.current.scrollTop = nextScrollHeight - previousScrollHeight + previousScrollTop;
      });
    }

    setLoadingOlderMessages(false);
  };

  const handleSend = async () => {
    const content = newMessage.trim();
    if (!content) return;

    const optimistic = {
      id: `opt-${Date.now()}`,
      chat_id: chatId,
      sender_id: user.id,
      content,
      created_at: new Date().toISOString(),
      profiles: profileCache.current[user.id] || null,
    };
    shouldStickToBottomRef.current = true;
    setMessages(prev => [...prev, optimistic]);
    setNewMessage('');

    await supabase.from('chat_messages').insert({ chat_id: chatId, sender_id: user.id, content });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMessagesScroll = () => {
    if (!messagesViewportRef.current || loadingOlderMessages || !hasMoreMessages) return;
    if (messagesViewportRef.current.scrollTop <= 80) {
      loadOlderMessages();
    }
  };

  const handleDeleteChat = async () => {
    setDeleting(true);
    const { error } = await supabase.rpc('delete_chat', { p_chat_id: chatId });
    setDeleting(false);
    if (!error && onDeleted) onDeleted();
  };

  const handleLeaveChat = async () => {
    setLeaving(true);
    const { error } = await supabase
      .from('chat_members')
      .delete()
      .eq('chat_id', accessChatId)
      .eq('user_id', user.id);
    setLeaving(false);
    if (!error) {
      setShowLeaveConfirm(false);
      if (onLeft) onLeft();
    }
  };

  const canDeleteMessage = (msg) => {
    if (String(msg.id).startsWith('opt-')) return false;
    return isOwner || msg.sender_id === user.id;
  };

  const handleDeleteMessage = async () => {
    if (!deleteTargetMessage) return;
    setDeletingMessage(true);
    const { error } = await supabase.from('chat_messages').delete().eq('id', deleteTargetMessage.id);
    setDeletingMessage(false);
    if (!error) {
      setMessages(prev => prev.filter(msg => msg.id !== deleteTargetMessage.id));
      setDeleteTargetMessage(null);
      setActiveMessageMenuId(null);
    }
  };

  const getSenderName = (msg) => {
    const p = msg.profiles;
    if (!p) return 'Unknown';
    return p.display_name || p.username || 'Unknown';
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwner = membership?.role === 'owner';
  const isBanned = membership?.banned;
  const isKicked = membership?.kicked_until && new Date(membership.kicked_until) > new Date();
  const canSend = !isBanned && !isKicked;
  const sharedVoiceParticipants = voicePresence?.participants || [];
  const sharedVoiceCount = voicePresence?.count || 0;
  const sharedSpeakingCount = voicePresence?.speakingCount || 0;
  const sharedVoiceNames = sharedVoiceParticipants.map((participant) => participant.displayName).join(', ');

  useEffect(() => {
    if (!chat || !inputRef.current || !canSend) return;
    const focusId = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(focusId);
  }, [chatId, chat, canSend]);

  if (!chat) {
    return (
      <div className="room-loading">
        <span>Loading room…</span>
      </div>
    );
  }

  return (
    <div className="room-chat-view">

      {/* ── Topbar ── */}
      <div className="room-topbar">
        <div className="room-topbar-title-group">
          <span className="room-topbar-name">{chat.name}</span>
          {sharedVoiceCount > 0 && (
            <span
              className={`room-topbar-voice-summary${sharedSpeakingCount > 0 ? ' is-speaking' : ''}`}
              title={sharedVoiceNames || `${sharedVoiceCount} in voice`}
            >
              {sharedSpeakingCount > 0 ? `${sharedSpeakingCount} speaking` : `${sharedVoiceCount} in voice`}
            </span>
          )}
          <RoomVoiceEntry roomId={chatId} user={user} roomName={chat.name} voicePresence={voicePresence} />
        </div>

        <div className="room-topbar-menu" ref={menuRef}>
          <button
            className="room-menu-trigger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Room options"
          >
            <span className="room-menu-label">Chat Actions</span>
            <span className="room-menu-dots">•••</span>
          </button>

          {menuOpen && (
            <div className="room-menu-dropdown">
              <button
                className="room-menu-item"
                onClick={() => { setMenuOpen(false); onToggleFavorite(); }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M7 1.5l1.545 3.13 3.455.502-2.5 2.437.59 3.441L7 9.25l-3.09 1.76.59-3.44L2 5.132l3.455-.502L7 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill={isFavorite ? 'currentColor' : 'none'}/>
                </svg>
                {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
              </button>
              <button
                className="room-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  if (isNotificationsMuted) {
                    onUnmuteNotifications();
                  } else {
                    setShowNotificationMuteModal(true);
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  {isNotificationsMuted ? (
                    <>
                      <path d="M7 2.25a3.25 3.25 0 0 1 3.25 3.25v1.5c0 .52.19 1.02.55 1.4l.45.48H2.75l.45-.48c.36-.38.55-.88.55-1.4V5.5A3.25 3.25 0 0 1 7 2.25Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                      <path d="M5.5 11a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </>
                  ) : (
                    <>
                      <path d="M7 2.25a3.25 3.25 0 0 1 3.25 3.25v1.5c0 .52.19 1.02.55 1.4l.45.48H2.75l.45-.48c.36-.38.55-.88.55-1.4V5.5A3.25 3.25 0 0 1 7 2.25Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                      <path d="M5.5 11a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      <path d="M2 2l10 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
                {isNotificationsMuted ? 'Unmute notifications' : 'Mute notifications'}
              </button>
              {!isOwner && (
                <button
                  className="room-menu-item danger"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowLeaveConfirm(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M5 2.5H3.75A1.25 1.25 0 0 0 2.5 3.75v6.5A1.25 1.25 0 0 0 3.75 11.5H5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 4.5 10.5 7 8 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5.5 7h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Leave Chat
                </button>
              )}
              {isOwner && (
                <button
                  className="room-menu-item danger"
                  onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4.5 3.5l.5 8h4l.5-8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Delete chat
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {(isBanned || isKicked) && (
        <div className="room-access-denied">
          {isBanned
            ? 'You have been banned from this room.'
            : `You are kicked until ${new Date(membership.kicked_until).toLocaleString()}.`}
        </div>
      )}

      {/* ── Messages ── */}
      <div className="messages-scroll-root" style={{ flex: 1, overflowY: 'auto' }}>
        <div
          ref={messagesViewportRef}
          className="messages-scroll-viewport"
          onScroll={handleMessagesScroll}
        >
          {messages.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon">💬</span>
              <span>No messages yet. Say hello!</span>
            </div>
          ) : (
            <div className="messages-list">
              {loadingOlderMessages && (
                <div className="messages-history-status">Loading earlier messages…</div>
              )}
              {!loadingOlderMessages && hasMoreMessages && (
                <div className="messages-history-status">Scroll up to load earlier messages</div>
              )}
              {groupMessages(messages).map(group => {
                const firstMsg = group.messages[0];
                return (
                  <div key={firstMsg.id} className="message-group">
                    <div
                      className="message-avatar"
                      style={{ background: getAvatarColor(firstMsg.sender_id) }}
                      aria-hidden="true"
                    >
                      {getSenderName(firstMsg).charAt(0).toUpperCase()}
                    </div>
                    <div className="message-group-body">
                      <div className="message-header">
                        <span className="message-sender-name">{getSenderName(firstMsg)}</span>
                        <span className="message-time">{formatTime(firstMsg.created_at)}</span>
                      </div>
                      {group.messages.map(msg => {
                        const canDelete = canDeleteMessage(msg);
                        return (
                          <div key={msg.id} className="message-item">
                            <p className="message-text">{msg.content}</p>
                            <div className={`message-actions${activeMessageMenuId === msg.id ? ' menu-open' : ''}`}>
                              <button
                                className="message-action-trigger"
                                onClick={() => setActiveMessageMenuId(current => current === msg.id ? null : msg.id)}
                                aria-label="Message actions"
                              >
                                <span className="message-action-dots">•••</span>
                              </button>
                              {activeMessageMenuId === msg.id && (
                                <div className="message-action-menu">
                                  {canDelete ? (
                                    <button
                                      className="message-action-item danger"
                                      onClick={() => {
                                        setDeleteTargetMessage(msg);
                                        setActiveMessageMenuId(null);
                                      }}
                                    >
                                      Delete Message
                                    </button>
                                  ) : (
                                    <div className="message-action-empty">No actions available yet</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* ── Input ── */}
      <div className="chat-input-area">
        <textarea
          ref={inputRef}
          className="chat-textarea"
          rows={2}
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={canSend ? 'Type a message… (Enter to send)' : 'You cannot send messages here.'}
          disabled={!canSend}
        />
        <button
          className="btn btn-primary btn-send"
          onClick={handleSend}
          disabled={!canSend || !newMessage.trim()}
        >
          Send
        </button>
      </div>

      {/* ── Delete Confirm Modal ── */}
      {showNotificationMuteModal && (
        <div className="room-modal-backdrop" onClick={() => setShowNotificationMuteModal(false)}>
          <div className="room-modal" onClick={e => e.stopPropagation()}>
            <h3 className="room-modal-title">Mute notifications</h3>
            <p className="room-modal-subtitle">For how long?</p>
            <div className="room-option-grid">
              {NOTIFICATION_MUTE_OPTIONS.map(option => (
                <button
                  key={option.label}
                  className={`dash-btn${option.minutes === null ? ' dash-btn-danger' : ' dash-btn-secondary'}`}
                  onClick={() => {
                    onMuteNotifications(option.minutes);
                    setShowNotificationMuteModal(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              className="dash-btn dash-btn-secondary room-modal-cancel"
              onClick={() => setShowNotificationMuteModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {deleteTargetMessage && (
        <div className="room-modal-backdrop" onClick={() => !deletingMessage && setDeleteTargetMessage(null)}>
          <div className="room-modal" onClick={e => e.stopPropagation()}>
            <h3 className="room-modal-title">Delete Message</h3>
            <p className="room-modal-subtitle">Are you sure you want to delete this message?</p>
            <div className="message-delete-preview">
              {deleteTargetMessage.content}
            </div>
            <div className="delete-modal-actions">
              <button
                className="dash-btn dash-btn-secondary"
                onClick={() => setDeleteTargetMessage(null)}
                disabled={deletingMessage}
              >
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-danger"
                onClick={handleDeleteMessage}
                disabled={deletingMessage}
              >
                {deletingMessage ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="room-modal-backdrop" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="room-modal" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="13" stroke="#dc2626" strokeWidth="1.5" strokeOpacity="0.4"/>
                <path d="M6 8h16M10.5 8V6.5h7V8M9 8l1 15h8l1-15" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="room-modal-title">Delete "{chat.name}"?</h3>
            <p className="room-modal-subtitle">
              Are you sure you wish to delete this chat? This cannot be undone.
            </p>
            <div className="delete-modal-actions">
              <button
                className="dash-btn dash-btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Nevermind
              </button>
              <button
                className="dash-btn dash-btn-danger"
                onClick={handleDeleteChat}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, delete chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLeaveConfirm && (
        <div className="room-modal-backdrop" onClick={() => !leaving && setShowLeaveConfirm(false)}>
          <div className="room-modal" onClick={e => e.stopPropagation()}>
            <h3 className="room-modal-title">Leave Chat</h3>
            <p className="room-modal-subtitle">Are you sure you want to leave this chat?</p>
            <div className="delete-modal-actions">
              <button
                className="dash-btn dash-btn-secondary"
                onClick={() => setShowLeaveConfirm(false)}
                disabled={leaving}
              >
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-danger"
                onClick={handleLeaveChat}
                disabled={leaving}
              >
                {leaving ? 'Leaving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RoomChat;
